import { useEffect, useRef, useState } from "react"
import type { RefObject } from "react"

interface UseScrollChevronsReturn {
  scrollRef: RefObject<HTMLDivElement | null>
  selectedRef: RefObject<HTMLButtonElement | null>
  canScroll: { left: boolean; right: boolean }
  scroll: (direction: 1 | -1) => void
}

/**
 * Scrolling with chevrons for a row of buttons that may not fit its box: which side still
 * has buttons to show, a half-box step for each chevron, and the selected button brought
 * into view, clear of the chevrons, whenever `selected` changes.
 */
const useScrollChevrons = (
  selected: unknown,
  chevronWidth: number,
): UseScrollChevronsReturn => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)
  const [canScroll, setCanScroll] = useState({ left: false, right: false })

  useEffect(() => {
    const element = scrollRef.current
    if (!element || typeof ResizeObserver === "undefined") return
    const update = () =>
      setCanScroll({
        left: element.scrollLeft > 0,
        right: element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
      })
    const observer = new ResizeObserver(update)
    observer.observe(element)
    // The buttons can change width without the box resizing, like when the language changes.
    if (element.firstElementChild) observer.observe(element.firstElementChild)
    element.addEventListener("scroll", update)
    return () => {
      observer.disconnect()
      element.removeEventListener("scroll", update)
    }
  }, [])

  useEffect(() => {
    const element = scrollRef.current
    const button = selectedRef.current
    if (!element || !button) return
    const start = button.offsetLeft - chevronWidth
    const end =
      button.offsetLeft + button.offsetWidth + chevronWidth - element.clientWidth
    if (start < element.scrollLeft) element.scrollTo({ left: start, behavior: "smooth" })
    else if (end > element.scrollLeft) element.scrollTo({ left: end, behavior: "smooth" })
  }, [selected, chevronWidth])

  const scroll = (direction: 1 | -1) => {
    const element = scrollRef.current
    element?.scrollBy({ left: (direction * element.clientWidth) / 2, behavior: "smooth" })
  }

  return { scrollRef, selectedRef, canScroll, scroll }
}

export default useScrollChevrons
